/**
 * Audio playback + Web Audio analyser for the spoken-response stream.
 *
 * Verbatim port of `legacy/storyscale-widgets/src/agent-chat-v2/mixins/AudioSpeech.js`.
 * Same FFT 256, smoothingTimeConstant 0.8, audioLevel = avg / 255 contract.
 *
 * The analyser drives a 0..1 `audioLevel` that callers wire into the
 * greeter's `--audio-level` CSS variable so the greeter visibly
 * "speaks" while the agent is talking.
 */
type Handlers = {
  /** 0..1 instantaneous level. Called every animation frame while playing. */
  onLevel?: (level: number) => void;
  onPlay?: () => void;
  onStop?: () => void;
  onError?: (err: unknown) => void;
};

type WindowWithWebkitAudio = typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

export interface AudioSpeechController {
  play: (audioSrc: string) => Promise<void>;
  stop: () => void;
  isPlaying: () => boolean;
  destroy: () => void;
}

export function createAudioSpeech(handlers: Handlers = {}): AudioSpeechController {
  let audioCtx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let source: MediaElementAudioSourceNode | null = null;
  let dataArray: Uint8Array<ArrayBuffer> | null = null;
  let rafId: number | null = null;
  let speechAudio: HTMLAudioElement | null = null;
  let analyzing = false;

  const initAnalyser = (): boolean => {
    try {
      if (!audioCtx) {
        const Ctor =
          window.AudioContext ?? (window as WindowWithWebkitAudio).webkitAudioContext;
        if (!Ctor) return false;
        audioCtx = new Ctor();
      }
      if (!analyser) {
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        dataArray = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
      }
      return true;
    } catch (err) {
      console.error('[showpilot/audio] init analyser failed', err);
      return false;
    }
  };

  const connect = (el: HTMLAudioElement): boolean => {
    if (!analyser && !initAnalyser()) return false;
    if (!audioCtx) return false;
    if (audioCtx.state === 'suspended') {
      void audioCtx.resume();
    }
    if (!source) {
      try {
        source = audioCtx.createMediaElementSource(el);
        source.connect(analyser!);
        analyser!.connect(audioCtx.destination);
      } catch (err) {
        if ((err as { name?: string })?.name === 'InvalidStateError') return true;
        console.error('[showpilot/audio] connect failed', err);
        return false;
      }
    }
    return true;
  };

  const tick = (): void => {
    if (!analyzing || !analyser || !dataArray) return;
    analyser.getByteFrequencyData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] ?? 0;
    const level = sum / dataArray.length / 255;
    handlers.onLevel?.(level);
    rafId = requestAnimationFrame(tick);
  };

  const startAnalysis = (): void => {
    if (analyzing) return;
    analyzing = true;
    tick();
  };

  const stopAnalysis = (): void => {
    analyzing = false;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    handlers.onLevel?.(0);
  };

  const cleanup = (): void => {
    if (source) {
      try {
        source.disconnect();
      } catch {
        // ignore — already disconnected
      }
      source = null;
    }
    if (audioCtx && audioCtx.state !== 'closed') {
      void audioCtx.close();
      audioCtx = null;
    }
    analyser = null;
    dataArray = null;
  };

  const stop = (): void => {
    if (speechAudio) {
      speechAudio.pause();
      speechAudio.currentTime = 0;
      speechAudio = null;
    }
    stopAnalysis();
    handlers.onStop?.();
    cleanup();
  };

  const play = (audioSrc: string): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      try {
        stop();
        const audio = new Audio(audioSrc);
        audio.crossOrigin = 'anonymous';

        audio.addEventListener('play', () => {
          connect(audio);
          startAnalysis();
          handlers.onPlay?.();
        });
        audio.addEventListener('ended', () => {
          stopAnalysis();
          handlers.onStop?.();
          speechAudio = null;
          resolve();
        });
        audio.addEventListener('error', (err) => {
          stopAnalysis();
          handlers.onError?.(err);
          handlers.onStop?.();
          speechAudio = null;
          reject(err);
        });

        speechAudio = audio;
        audio.play().catch((err) => {
          handlers.onError?.(err);
          reject(err);
        });
      } catch (err) {
        handlers.onError?.(err);
        handlers.onStop?.();
        reject(err);
      }
    });

  return {
    play,
    stop,
    isPlaying: () => speechAudio !== null,
    destroy: () => stop(),
  };
}
